import React, { useState } from 'react';
import {
    Box,
    Grid,
    Typography,
    Divider,
    IconButton,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
} from '@mui/material';
import { useThemeContext } from '../../../../hook/useThemeManager';
import { theme } from '../../../../constants/theme';
import { FONT_ENUM } from '../../../../types/global/fonts.types';
import {
    EditRounded,
    CheckCircleOutline,
    Cancel,
    UploadFile,
    PictureAsPdf,
    Delete,
    Visibility,
} from '@mui/icons-material';
import { IAudition } from '../../../../types/global/helper';
import { CustomDatePicker } from '../../../../components/DatePicker/CustomDatePicker';
import { useDispatch } from 'react-redux';
import { updateAudition } from '../../../../redux/audition/actions';
import { useGoogleMapsLoader } from '../../../../hook/useMapLoader';
import { InfoTextField } from '../../../../components/Common/InfoTextField';
import { PlacesAutocompleteField } from '../../../Common/PlacesAutocompleteField';
import { currencySymbols } from '../../../../constants/currency';
import { CompanyAvatar } from '../../../Avatar/CompanyAvatar';
import SearchSelectComponent from '../../../SearchAdd/SearchSelectComponent';
import { RichTextViewer } from '../../../Common/RichTextViewer';

interface AuditionDetailsTabProps {
    audition?: IAudition;
    onEditDetails: (sectionName: string) => void;
}

export const AuditionDetailsTab: React.FC<AuditionDetailsTabProps> = ({ audition, onEditDetails }) => {
    const { themeStyle } = useThemeContext();
    const dispatch = useDispatch();
    const { isLoaded } = useGoogleMapsLoader();

    // State for editing sections
    const [editingSections, setEditingSections] = useState<{ [key: string]: boolean }>({
        details: false,
        description: false,
        requirements: false,
        compensation: false,
        location: false,
        dates: false,
        attachments: false,
        submission_report: false,
        production_company: false,
        tags: false,
        union_affiliation: false,
        type_of_work: false,
        usage: false,
    });

    // State for edited values
    const [editedValues, setEditedValues] = useState<{ [key: string]: any }>({});

    if (!audition) {
        return (
            <Typography
                sx={{
                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                    fontSize: '1rem',
                    color: theme[themeStyle].primary_white,
                }}>
                No audition details available
            </Typography>
        );
    }

    const handleStartEditing = (section: string) => {
        // Initialize edited values when starting to edit a section
        let initialValues: any = {};

        switch (section) {
            case 'details':
                initialValues = {
                    title: audition.title,
                    synopsis: audition.synopsis || '',
                    plot: audition.plot || '',
                    company_website: audition.company_website || '',
                    production_personel: audition.production_personel || '',
                };
                break;
            case 'description':
                initialValues = {
                    description: audition.description || '',
                };
                break;
            case 'requirements':
                initialValues = {
                    requirements: audition.requirements || [],
                    required_media: audition.required_media || [],
                    required_skill: audition.required_skill || [],
                    min_age: audition.min_age || 1,
                    max_age: audition.max_age || 100,
                };
                break;
            case 'compensation':
                initialValues = {
                    compensation: audition.compensation || 'unpaid',
                    employment_type: audition.employment_type || 'one-off',
                    reward: audition.reward || 0,
                    min_reward: audition.min_reward || 0,
                    max_reward: audition.max_reward || 0,
                    reward_frequency: audition.reward_frequency || 'gig',
                    currency: audition.currency || 'USD',
                };
                break;
            case 'location':
                initialValues = {
                    shoot_location: audition.shoot_location || {
                        type: 'Point',
                        coordinates: [0, 0],
                        address: 'worldwide',
                        description: 'remote',
                    },
                    work_location: audition.work_location || [],
                    talent_location: audition.talent_location || [],
                };
                break;
            case 'dates':
                initialValues = {
                    job_start_date: audition.job_start_date || '',
                    job_end_date: audition.job_end_date || '',
                    rehearsal_date: audition.rehearsal_date || '',
                    casting_expiration: audition.casting_expiration || '',
                };
                break;
            case 'attachments':
                initialValues = {
                    attachment: audition.attachment || '',
                };
                break;
            case 'submission_report':
                initialValues = {
                    submission_report: audition.submission_report || 'daily digest',
                };
                break;
            case 'production_company':
                initialValues = {
                    production_company: audition.production_company || null,
                };
                break;
            case 'tags':
                initialValues = {
                    tags: audition.tags || [],
                };
                break;
            case 'union_affiliation':
                initialValues = {
                    union_affiliation: audition.union_affiliation || '',
                };
                break;
            case 'type_of_work':
                initialValues = {
                    type_of_work: audition.type_of_work || '',
                };
                break;
            case 'usage':
                initialValues = {
                    usage: audition.usage || [],
                };
                break;
        }

        setEditedValues({ ...editedValues, ...initialValues });
        setEditingSections({ ...editingSections, [section]: true });
    };

    const handleCancelEditing = (section: string) => {
        setEditingSections({ ...editingSections, [section]: false });
    };

    const handleSaveChanges = (section: string) => {
        let updatePayload: any = {};

        switch (section) {
            case 'details':
                updatePayload = {
                    title: editedValues.title,
                    synopsis: editedValues.synopsis,
                    plot: editedValues.plot,
                    company_website: editedValues.company_website,
                    production_personel: editedValues.production_personel,
                };
                break;
            case 'description':
                updatePayload = {
                    description: editedValues.description,
                };
                break;
            case 'requirements':
                updatePayload = {
                    requirements: editedValues.requirements,
                    required_media: editedValues.required_media,
                    required_skill: editedValues.required_skill,
                    min_age: editedValues.min_age,
                    max_age: editedValues.max_age,
                };
                break;
            case 'compensation':
                updatePayload = {
                    compensation: editedValues.compensation,
                    employment_type: editedValues.employment_type,
                    reward: editedValues.reward,
                    min_reward: editedValues.min_reward,
                    max_reward: editedValues.max_reward,
                    reward_frequency: editedValues.reward_frequency,
                    currency: editedValues.currency,
                };
                break;
            case 'location':
                updatePayload = {
                    shoot_location: editedValues.shoot_location,
                    work_location: editedValues.work_location,
                    talent_location: editedValues.talent_location,
                };
                break;
            case 'dates':
                updatePayload = {
                    job_start_date: editedValues.job_start_date,
                    job_end_date: editedValues.job_end_date,
                    rehearsal_date: editedValues.rehearsal_date,
                    casting_expiration: editedValues.casting_expiration,
                };
                break;
            case 'attachments':
                updatePayload = {
                    attachment: editedValues.attachment,
                    attachment_description: editedValues.attachment_description,
                    attachment_type: editedValues.attachment_type,
                };
                break;
            case 'submission_report':
                updatePayload = {
                    submission_report: editedValues.submission_report,
                };
                break;
            case 'production_company':
                updatePayload = {
                    production_company: editedValues.production_company,
                };
                break;
            case 'tags':
                updatePayload = {
                    tags: editedValues.tags,
                };
                break;
            case 'union_affiliation':
                updatePayload = {
                    union_affiliation: editedValues.union_affiliation,
                };
                break;
            case 'type_of_work':
                updatePayload = {
                    type_of_work: editedValues.type_of_work,
                };
                break;
            case 'usage':
                updatePayload = {
                    usage: editedValues.usage,
                };
                break;
        }

        // Dispatch update audition action
        if (audition._id) {
            dispatch(
                updateAudition({
                    id: audition._id,
                    body: updatePayload,
                    isImageUpload: false,
                }),
            );
        }

        // Turn off editing for this section
        setEditingSections({ ...editingSections, [section]: false });
    };

    const handleInputChange = (field: string, value: any) => {
        setEditedValues({ ...editedValues, [field]: value });
    };

    const SectionHeader = ({ title, section }: { title: string; section: string }) => (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography
                sx={{
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    fontSize: '1.25rem',
                    color: theme[themeStyle].primary_white,
                }}>
                {title}
            </Typography>

            {editingSections[section] ? (
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        startIcon={<Cancel />}
                        onClick={() => handleCancelEditing(section)}
                        sx={{
                            borderColor: theme[themeStyle].placeholderGray,
                            color: theme[themeStyle].primary_white,
                            fontSize: '0.875rem',
                            '&:hover': {
                                borderColor: theme[themeStyle].placeholderGray,
                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            },
                        }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<CheckCircleOutline />}
                        onClick={() => handleSaveChanges(section)}
                        sx={{
                            backgroundColor: theme[themeStyle].primary_orange,
                            color: theme[themeStyle].primary_white,
                            fontSize: '0.875rem',
                            '&:hover': {
                                backgroundColor: theme[themeStyle].primary_orange,
                                opacity: 0.9,
                            },
                        }}>
                        Save
                    </Button>
                </Box>
            ) : (
                <IconButton
                    onClick={() => {
                        handleStartEditing(section);
                        onEditDetails(section);
                    }}
                    sx={{
                        color: theme[themeStyle].primary_orange,
                    }}>
                    <EditRounded />
                </IconButton>
            )}
        </Box>
    );

    const availableTags = [
        'paid',
        'unpaid',
        'nollywood',
        'short film',
        'feature',
        'photo shoot',
        'event',
        'student film',
        'ghallywood',
        'hollywood',
        'music video',
    ];

    return (
        <Box
            sx={{
                py: 3,
                bgcolor: theme[themeStyle].pure_black,
                borderRadius: 2,
                width: '100%',
            }}>
            {/* Audition Details */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader title="Details" section="details" />

                {!editingSections.details ? (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Title
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                {audition.title}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Job Description
                            </Typography>
                            <RichTextViewer
                                content={audition.synopsis || 'No synopsis provided'}
                                textStyle={{ padding: 0 }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Additional Details
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                {audition.plot || 'No plot provided'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Company Website
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                {audition.company_website || 'No website provided'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Production Personnel
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                {audition.production_personel || 'No personnel information provided'}
                            </Typography>
                        </Grid>
                    </Grid>
                ) : (
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Title
                            </Typography>
                            <TextField
                                fullWidth
                                placeholder="Audition Title"
                                value={editedValues.title || ''}
                                onChange={e => handleInputChange('title', e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Job Description
                            </Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                placeholder="Job Description or Synopsis"
                                value={editedValues.synopsis || ''}
                                onChange={e => handleInputChange('synopsis', e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Additional Details / Plot
                            </Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                placeholder="Additional Details / Plot"
                                value={editedValues.plot || ''}
                                onChange={e => handleInputChange('plot', e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Company Website
                            </Typography>
                            <TextField
                                fullWidth
                                placeholder="Company Website URL"
                                value={editedValues.company_website || ''}
                                onChange={e => handleInputChange('company_website', e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Production Personnel
                            </Typography>
                            <TextField
                                fullWidth
                                placeholder="Production Personnel"
                                value={editedValues.production_personel || ''}
                                onChange={e => handleInputChange('production_personel', e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                }}
                            />
                        </Grid>
                    </Grid>
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].form_black }} />

            {/* Production Company */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader title="Production Company" section="production_company" />

                {!editingSections.production_company ? (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            {audition.production_company ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    {audition.production_company && audition.production_company._id && (
                                        <CompanyAvatar company={audition.production_company} size="medium" />
                                    )}
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                fontSize: '16px',
                                                color: theme[themeStyle].primary_white,
                                            }}>
                                            {audition.production_company.company_name}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                fontSize: '14px',
                                                color: theme[themeStyle].light_gray_text,
                                            }}>
                                            {audition.production_company.company_type || 'Production Company'}
                                        </Typography>
                                    </Box>
                                </Box>
                            ) : (
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        color: theme[themeStyle].light_gray_text,
                                    }}>
                                    No production company assigned
                                </Typography>
                            )}
                        </Grid>
                    </Grid>
                ) : (
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <SearchSelectComponent
                                onChange={value => handleInputChange('production_company', value)}
                                onAddClick={() => {
                                    // Handle add new company logic here
                                    console.log('Add new company clicked');
                                }}
                                placeholder="Search for production company..."
                                category="company"
                                initialValue={editedValues.production_company}
                            />
                        </Grid>
                    </Grid>
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].form_black }} />

            {/* Tags */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader title="Tags" section="tags" />

                {!editingSections.tags ? (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            {audition.tags && audition.tags.length > 0 ? (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {audition.tags.map((tag, index) => (
                                        <Chip
                                            key={index}
                                            label={tag}
                                            sx={{
                                                backgroundColor: theme[themeStyle].background_gradient.end,
                                                color: theme[themeStyle].primary_white,
                                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                fontSize: '0.875rem',
                                            }}
                                        />
                                    ))}
                                </Box>
                            ) : (
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        color: theme[themeStyle].light_gray_text,
                                    }}>
                                    No tags assigned
                                </Typography>
                            )}
                        </Grid>
                    </Grid>
                ) : (
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel sx={{ color: theme[themeStyle].tag_white }}>Tags</InputLabel>
                                <Select
                                    multiple
                                    value={editedValues.tags || []}
                                    onChange={e => handleInputChange('tags', e.target.value)}
                                    label="Tags"
                                    renderValue={selected => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map((value: any) => (
                                                <Chip
                                                    key={value}
                                                    label={value}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: theme[themeStyle].background_gradient.end,
                                                        color: theme[themeStyle].primary_white,
                                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                        fontSize: '0.75rem',
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    )}
                                    sx={{
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                        '& .MuiSelect-select': {
                                            backgroundColor: theme[themeStyle].gray_text,
                                        },
                                        '& .MuiSvgIcon-root': {
                                            color: theme[themeStyle].primary_white,
                                        },
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: theme[themeStyle].gray_text,
                                                color: theme[themeStyle].primary_white,
                                                '& .MuiMenuItem-root': {
                                                    color: theme[themeStyle].primary_white,
                                                    '&:hover': {
                                                        backgroundColor: theme[themeStyle].background_gradient.end,
                                                    },
                                                    '&.Mui-selected': {
                                                        backgroundColor: theme[themeStyle].primary_orange,
                                                        color: theme[themeStyle].black,
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].primary_orange,
                                                        },
                                                    },
                                                },
                                            },
                                        },
                                    }}>
                                    {availableTags.map(tag => (
                                        <MenuItem key={tag} value={tag}>
                                            {tag}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].form_black }} />

            {/* Union Affiliation */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader title="Union Affiliation" section="union_affiliation" />

                {!editingSections.union_affiliation ? (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            {audition.union_affiliation ? (
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        color: theme[themeStyle].primary_white,
                                    }}>
                                    {audition.union_affiliation}
                                </Typography>
                            ) : (
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        color: theme[themeStyle].light_gray_text,
                                    }}>
                                    No union affiliation specified
                                </Typography>
                            )}
                        </Grid>
                    </Grid>
                ) : (
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel sx={{ color: theme[themeStyle].tag_white }}>Union Affiliation</InputLabel>
                                <Select
                                    value={editedValues.union_affiliation || ''}
                                    onChange={e => handleInputChange('union_affiliation', e.target.value)}
                                    label="Union Affiliation"
                                    sx={{
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                        '& .MuiSelect-select': {
                                            backgroundColor: theme[themeStyle].gray_text,
                                        },
                                        '& .MuiSvgIcon-root': {
                                            color: theme[themeStyle].primary_white,
                                        },
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: theme[themeStyle].gray_text,
                                                color: theme[themeStyle].primary_white,
                                                '& .MuiMenuItem-root': {
                                                    color: theme[themeStyle].primary_white,
                                                    '&:hover': {
                                                        backgroundColor: theme[themeStyle].background_gradient.end,
                                                    },
                                                    '&.Mui-selected': {
                                                        backgroundColor: theme[themeStyle].primary_orange,
                                                        color: theme[themeStyle].black,
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].primary_orange,
                                                        },
                                                    },
                                                },
                                            },
                                        },
                                    }}>
                                    <MenuItem value="AEA">AEA</MenuItem>
                                    <MenuItem value="SAG-AFTRA">SAG-AFTRA</MenuItem>
                                    <MenuItem value="non-union">Non-Union</MenuItem>
                                    <MenuItem value="not applicable">Not Applicable</MenuItem>
                                    <MenuItem value="ACTRA">ACTRA</MenuItem>
                                    <MenuItem value="CAEA">CAEA</MenuItem>
                                    <MenuItem value="Equity">Equity</MenuItem>
                                    <MenuItem value="MEAA">MEAA</MenuItem>
                                    <MenuItem value="non-union affiliation">Non-Union Affiliation</MenuItem>
                                    <MenuItem value="other">Other</MenuItem>
                                    <MenuItem value="UBCP/ACTRA">UBCP/ACTRA</MenuItem>
                                    <MenuItem value="UDA">UDA</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].form_black }} />

            {/* Type of Work */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader title="Type of Work" section="type_of_work" />

                {!editingSections.type_of_work ? (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            {audition.type_of_work ? (
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        color: theme[themeStyle].primary_white,
                                    }}>
                                    {audition.type_of_work}
                                </Typography>
                            ) : (
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        color: theme[themeStyle].light_gray_text,
                                    }}>
                                    No type of work specified
                                </Typography>
                            )}
                        </Grid>
                    </Grid>
                ) : (
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel sx={{ color: theme[themeStyle].tag_white }}>Type of Work</InputLabel>
                                <Select
                                    value={editedValues.type_of_work || ''}
                                    onChange={e => handleInputChange('type_of_work', e.target.value)}
                                    label="Type of Work"
                                    sx={{
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                        '& .MuiSelect-select': {
                                            backgroundColor: theme[themeStyle].gray_text,
                                        },
                                        '& .MuiSvgIcon-root': {
                                            color: theme[themeStyle].primary_white,
                                        },
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: theme[themeStyle].gray_text,
                                                color: theme[themeStyle].primary_white,
                                                '& .MuiMenuItem-root': {
                                                    color: theme[themeStyle].primary_white,
                                                    '&:hover': {
                                                        backgroundColor: theme[themeStyle].background_gradient.end,
                                                    },
                                                    '&.Mui-selected': {
                                                        backgroundColor: theme[themeStyle].primary_orange,
                                                        color: theme[themeStyle].black,
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].primary_orange,
                                                        },
                                                    },
                                                },
                                            },
                                        },
                                    }}>
                                    <MenuItem value="ad campaign">Ad Campaign</MenuItem>
                                    <MenuItem value="short">Short</MenuItem>
                                    <MenuItem value="commercial">Commercial</MenuItem>
                                    <MenuItem value="vertical short form">Vertical Short Form</MenuItem>
                                    <MenuItem value="episodic">Episodic</MenuItem>
                                    <MenuItem value="student film">Student Film</MenuItem>
                                    <MenuItem value="theatre">Theatre</MenuItem>
                                    <MenuItem value="director's reel">Director's Reel</MenuItem>
                                    <MenuItem value="feature">Feature</MenuItem>
                                    <MenuItem value="print">Print</MenuItem>
                                    <MenuItem value="pilot presentation">Pilot Presentation</MenuItem>
                                    <MenuItem value="table read">Table Read</MenuItem>
                                    <MenuItem value="live event">Live Event</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].form_black }} />

            {/* Usage */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader title="Usage" section="usage" />

                {!editingSections.usage ? (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            {audition.usage && audition.usage.length > 0 ? (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {audition.usage.map((usageType, index) => (
                                        <Chip
                                            key={index}
                                            label={usageType}
                                            sx={{
                                                backgroundColor: theme[themeStyle].background_gradient.end,
                                                color: theme[themeStyle].primary_white,
                                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                fontSize: '0.875rem',
                                            }}
                                        />
                                    ))}
                                </Box>
                            ) : (
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        color: theme[themeStyle].light_gray_text,
                                    }}>
                                    No usage specified
                                </Typography>
                            )}
                        </Grid>
                    </Grid>
                ) : (
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel sx={{ color: theme[themeStyle].tag_white }}>Usage</InputLabel>
                                <Select
                                    multiple
                                    value={editedValues.usage || []}
                                    onChange={e => handleInputChange('usage', e.target.value)}
                                    label="Usage"
                                    renderValue={selected => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map((value: any) => (
                                                <Chip
                                                    key={value}
                                                    label={value}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: theme[themeStyle].background_gradient.end,
                                                        color: theme[themeStyle].primary_white,
                                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                        fontSize: '0.75rem',
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    )}
                                    sx={{
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                        '& .MuiSelect-select': {
                                            backgroundColor: theme[themeStyle].gray_text,
                                        },
                                        '& .MuiSvgIcon-root': {
                                            color: theme[themeStyle].primary_white,
                                        },
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: theme[themeStyle].gray_text,
                                                color: theme[themeStyle].primary_white,
                                                '& .MuiMenuItem-root': {
                                                    color: theme[themeStyle].primary_white,
                                                    '&:hover': {
                                                        backgroundColor: theme[themeStyle].background_gradient.end,
                                                    },
                                                    '&.Mui-selected': {
                                                        backgroundColor: theme[themeStyle].primary_orange,
                                                        color: theme[themeStyle].black,
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].primary_orange,
                                                        },
                                                    },
                                                },
                                            },
                                        },
                                    }}>
                                    <MenuItem value="all media">All Media</MenuItem>
                                    <MenuItem value="internet">Internet</MenuItem>
                                    <MenuItem value="national broadcast">National Broadcast</MenuItem>
                                    <MenuItem value="international">International</MenuItem>
                                    <MenuItem value="cinema">Cinema</MenuItem>
                                    <MenuItem value="personal use">Personal Use</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].form_black }} />

            {/* Compensation */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader title="Compensation" section="compensation" />

                {!editingSections.compensation ? (
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Compensation Type
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                {audition.compensation || 'Unpaid'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Employment Type
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                {audition.employment_type || 'One-off'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Reward Range
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                {audition.min_reward && audition.max_reward
                                    ? `${audition.currency || 'USD'} ${Number(
                                          audition.min_reward,
                                      ).toLocaleString()} - ${Number(audition.max_reward).toLocaleString()}`
                                    : audition.reward
                                    ? `${audition.currency || 'USD'} ${Number(audition.reward).toLocaleString()}`
                                    : 'Not specified'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Reward Frequency
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                {audition.reward_frequency || 'Per gig'}
                            </Typography>
                        </Grid>
                    </Grid>
                ) : (
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                fullWidth
                                value={editedValues.compensation || 'unpaid'}
                                onChange={e => handleInputChange('compensation', e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                    '& .MuiSelect-select': {
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                }}
                                SelectProps={{
                                    MenuProps: {
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: theme[themeStyle].gray_text,
                                                color: theme[themeStyle].primary_white,
                                                '& .MuiMenuItem-root': {
                                                    color: theme[themeStyle].primary_white,
                                                    '&:hover': {
                                                        backgroundColor: theme[themeStyle].background_gradient.end,
                                                    },
                                                    '&.Mui-selected': {
                                                        backgroundColor: theme[themeStyle].primary_orange,
                                                        color: theme[themeStyle].black,
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].primary_orange,
                                                        },
                                                    },
                                                },
                                            },
                                        },
                                    },
                                    sx: {
                                        '& .MuiSvgIcon-root': {
                                            color: theme[themeStyle].primary_white,
                                        },
                                    },
                                }}>
                                <MenuItem value="paid">Paid</MenuItem>
                                <MenuItem value="unpaid">Unpaid</MenuItem>
                                <MenuItem value="tbd">To Be Determined</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                fullWidth
                                value={editedValues.employment_type || 'one-off'}
                                onChange={e => handleInputChange('employment_type', e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                    '& .MuiSelect-select': {
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                }}
                                SelectProps={{
                                    MenuProps: {
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: theme[themeStyle].gray_text,
                                                color: theme[themeStyle].primary_white,
                                                '& .MuiMenuItem-root': {
                                                    color: theme[themeStyle].primary_white,
                                                    '&:hover': {
                                                        backgroundColor: theme[themeStyle].background_gradient.end,
                                                    },
                                                    '&.Mui-selected': {
                                                        backgroundColor: theme[themeStyle].primary_orange,
                                                        color: theme[themeStyle].black,
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].primary_orange,
                                                        },
                                                    },
                                                },
                                            },
                                        },
                                    },
                                    sx: {
                                        '& .MuiSvgIcon-root': {
                                            color: theme[themeStyle].primary_white,
                                        },
                                    },
                                }}>
                                <MenuItem value="contract">Contract</MenuItem>
                                <MenuItem value="full time">Full Time</MenuItem>
                                <MenuItem value="one-off">One-off</MenuItem>
                                <MenuItem value="charity">Charity</MenuItem>
                                <MenuItem value="ongoing">Ongoing</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Min Reward"
                                value={editedValues.min_reward || ''}
                                onChange={e => handleInputChange('min_reward', e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <Typography
                                            sx={{
                                                color: theme[themeStyle].tag_white,
                                                mr: 1,
                                            }}>
                                            {editedValues.currency || 'USD'}
                                        </Typography>
                                    ),
                                }}
                                InputLabelProps={{
                                    style: {
                                        color: theme[themeStyle].tag_white,
                                    },
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                        '& .MuiInputLabel-root': {
                                            color: theme[themeStyle].tag_white,
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].tag_white,
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].tag_white,
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].primary_orange,
                                        },
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Max Reward"
                                value={editedValues.max_reward || ''}
                                onChange={e => handleInputChange('max_reward', e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <Typography
                                            sx={{
                                                color: theme[themeStyle].tag_white,
                                                mr: 1,
                                            }}>
                                            {editedValues.currency || 'USD'}
                                        </Typography>
                                    ),
                                }}
                                InputLabelProps={{
                                    style: {
                                        color: theme[themeStyle].tag_white,
                                    },
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                        '& .MuiInputLabel-root': {
                                            color: theme[themeStyle].tag_white,
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].tag_white,
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].tag_white,
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].primary_orange,
                                        },
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                select
                                fullWidth
                                value={editedValues.currency || 'USD'}
                                onChange={e => handleInputChange('currency', e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                    '& .MuiSelect-select': {
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                }}
                                SelectProps={{
                                    MenuProps: {
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: theme[themeStyle].gray_text,
                                                color: theme[themeStyle].primary_white,
                                                '& .MuiMenuItem-root': {
                                                    color: theme[themeStyle].primary_white,
                                                    '&:hover': {
                                                        backgroundColor: theme[themeStyle].background_gradient.end,
                                                    },
                                                    '&.Mui-selected': {
                                                        backgroundColor: theme[themeStyle].primary_orange,
                                                        color: theme[themeStyle].black,
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].primary_orange,
                                                        },
                                                    },
                                                },
                                            },
                                        },
                                    },
                                    sx: {
                                        '& .MuiSvgIcon-root': {
                                            color: theme[themeStyle].primary_white,
                                        },
                                    },
                                }}>
                                {Object.keys(currencySymbols).map(sym => (
                                    <MenuItem value={sym}>
                                        {sym} {currencySymbols[sym]}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                select
                                fullWidth
                                value={editedValues.reward_frequency || 'gig'}
                                onChange={e => handleInputChange('reward_frequency', e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                    '& .MuiSelect-select': {
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                }}
                                SelectProps={{
                                    MenuProps: {
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: theme[themeStyle].gray_text,
                                                color: theme[themeStyle].primary_white,
                                                '& .MuiMenuItem-root': {
                                                    color: theme[themeStyle].primary_white,
                                                    '&:hover': {
                                                        backgroundColor: theme[themeStyle].background_gradient.end,
                                                    },
                                                    '&.Mui-selected': {
                                                        backgroundColor: theme[themeStyle].primary_orange,
                                                        color: theme[themeStyle].black,
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].primary_orange,
                                                        },
                                                    },
                                                },
                                            },
                                        },
                                    },
                                    sx: {
                                        '& .MuiSvgIcon-root': {
                                            color: theme[themeStyle].primary_white,
                                        },
                                    },
                                }}>
                                <MenuItem value="monthly">Monthly</MenuItem>
                                <MenuItem value="weekly">Weekly</MenuItem>
                                <MenuItem value="daily">Daily</MenuItem>
                                <MenuItem value="yearly">Yearly</MenuItem>
                                <MenuItem value="gig">Per Gig</MenuItem>
                            </TextField>
                        </Grid>
                    </Grid>
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].form_black }} />

            {/* Location */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader title="Location" section="location" />

                {!editingSections.location ? (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Shoot Location
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                {audition.shoot_location?.address || 'Worldwide'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Work Location
                            </Typography>
                            {Array.isArray(audition.work_location) && audition.work_location.length > 0 ? (
                                <Box component="div" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {audition.work_location.map((location, index) => (
                                        <Box
                                            key={index}
                                            component="div"
                                            sx={{
                                                backgroundColor: theme[themeStyle].background_gradient.end,
                                                borderRadius: 1,
                                                padding: '4px 8px',
                                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                fontSize: '0.875rem',
                                                color: theme[themeStyle].primary_white,
                                            }}>
                                            {location.address}
                                        </Box>
                                    ))}
                                </Box>
                            ) : (
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        color: theme[themeStyle].primary_white,
                                    }}>
                                    {audition.work_location?.address || 'Not specified'}
                                </Typography>
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Talent Location
                            </Typography>
                            {Array.isArray(audition.talent_location) && audition.talent_location.length > 0 ? (
                                <Box component="div" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {audition.talent_location.map((location, index) => (
                                        <Box
                                            key={index}
                                            component="div"
                                            sx={{
                                                backgroundColor: theme[themeStyle].background_gradient.end,
                                                borderRadius: 1,
                                                padding: '4px 8px',
                                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                fontSize: '0.875rem',
                                                color: theme[themeStyle].primary_white,
                                            }}>
                                            {location.address}
                                        </Box>
                                    ))}
                                </Box>
                            ) : (
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        color: theme[themeStyle].primary_white,
                                    }}>
                                    {audition.talent_location?.address || 'Not specified'}
                                </Typography>
                            )}
                        </Grid>
                    </Grid>
                ) : (
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            {isLoaded ? (
                                <PlacesAutocompleteField
                                    value={editedValues.shoot_location}
                                    label="Shoot Location"
                                    onChange={location => handleInputChange('shoot_location', location)}
                                />
                            ) : (
                                <InfoTextField
                                    label="Shoot Location"
                                    infoText="The primary location where the audition or shoot will take place"
                                    value={editedValues.shoot_location?.address || ''}
                                    onChange={value =>
                                        handleInputChange('shoot_location', {
                                            ...editedValues.shoot_location,
                                            address: value,
                                        })
                                    }
                                />
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            {isLoaded ? (
                                <PlacesAutocompleteField
                                    value={editedValues.work_location}
                                    label="Work Location"
                                    onChange={locations => {
                                        // Ensure we're always working with an array
                                        const updatedLocations = Array.isArray(locations) ? locations : [locations];
                                        handleInputChange('work_location', updatedLocations);
                                    }}
                                    multiple
                                />
                            ) : (
                                <InfoTextField
                                    label="Work Location"
                                    infoText="The location where the actual work will be performed"
                                    value={editedValues.work_location?.address || ''}
                                    onChange={value =>
                                        handleInputChange('work_location', {
                                            ...editedValues.work_location,
                                            address: value,
                                        })
                                    }
                                />
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            {isLoaded ? (
                                <PlacesAutocompleteField
                                    value={editedValues.talent_location}
                                    label="Talent Location"
                                    onChange={locations => {
                                        // Ensure we're always working with an array
                                        const updatedLocations = Array.isArray(locations) ? locations : [locations];
                                        handleInputChange('talent_location', updatedLocations);
                                    }}
                                    multiple
                                />
                            ) : (
                                <InfoTextField
                                    label="Talent Location"
                                    infoText="The location where talent should be based or available"
                                    value={editedValues.talent_location?.address || ''}
                                    onChange={value =>
                                        handleInputChange('talent_location', {
                                            ...editedValues.talent_location,
                                            address: value,
                                        })
                                    }
                                />
                            )}
                        </Grid>
                    </Grid>
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].form_black }} />

            {/* Dates */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader title="Dates" section="dates" />

                {!editingSections.dates ? (
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Job Start Date
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                {audition.job_start_date
                                    ? new Date(audition.job_start_date).toLocaleDateString()
                                    : 'Not specified'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Job End Date
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                {audition.job_end_date
                                    ? new Date(audition.job_end_date).toLocaleDateString()
                                    : 'Not specified'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Rehearsal Date
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                {audition.rehearsal_date
                                    ? new Date(audition.rehearsal_date).toLocaleDateString()
                                    : 'Not specified'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Casting Expiration
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                {audition.casting_expiration
                                    ? new Date(audition.casting_expiration).toLocaleDateString()
                                    : 'Not specified'}
                            </Typography>
                        </Grid>
                    </Grid>
                ) : (
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={6}>
                            <CustomDatePicker
                                label="Job Start Date"
                                value={editedValues.job_start_date || ''}
                                onChange={(date, timezone) => {
                                    handleInputChange('job_start_date', date);
                                    handleInputChange('timezone', timezone);
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme[themeStyle].tag_white,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <CustomDatePicker
                                label="Job End Date"
                                value={editedValues.job_end_date || ''}
                                onChange={(date, timezone) => {
                                    handleInputChange('job_end_date', date);
                                    handleInputChange('timezone', timezone);
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme[themeStyle].tag_white,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <CustomDatePicker
                                label="Rehearsal Date"
                                value={editedValues.rehearsal_date || ''}
                                onChange={(date, timezone) => {
                                    handleInputChange('rehearsal_date', date);
                                    handleInputChange('timezone', timezone);
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme[themeStyle].tag_white,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <CustomDatePicker
                                label="Casting Expiration"
                                value={editedValues.casting_expiration || ''}
                                onChange={(date, timezone) => {
                                    handleInputChange('casting_expiration', date);
                                    if (timezone) {
                                        handleInputChange('timezone', timezone);
                                    }
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme[themeStyle].tag_white,
                                    },
                                }}
                            />
                        </Grid>
                    </Grid>
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].form_black }} />

            {/* Requirements */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader title="Requirements" section="requirements" />

                {!editingSections.requirements ? (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Required Media
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                {audition.required_media?.join(', ') || 'None specified'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Required Skills
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                {audition.required_skill?.join(', ') || 'None specified'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Age Range
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                {audition.min_age && audition.max_age
                                    ? `${audition.min_age} - ${audition.max_age} years`
                                    : 'Not specified'}
                            </Typography>
                        </Grid>
                    </Grid>
                ) : (
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel sx={{ color: theme[themeStyle].tag_white }}>Required Media</InputLabel>
                                <Select
                                    multiple
                                    value={editedValues.required_media || []}
                                    onChange={e => handleInputChange('required_media', e.target.value)}
                                    label="Required Media"
                                    sx={{
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                        '& .MuiSelect-select': {
                                            backgroundColor: theme[themeStyle].gray_text,
                                        },
                                        '& .MuiSvgIcon-root': {
                                            color: theme[themeStyle].primary_white,
                                        },
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: theme[themeStyle].gray_text,
                                                color: theme[themeStyle].primary_white,
                                                '& .MuiMenuItem-root': {
                                                    color: theme[themeStyle].primary_white,
                                                    '&:hover': {
                                                        backgroundColor: theme[themeStyle].background_gradient.end,
                                                    },
                                                    '&.Mui-selected': {
                                                        backgroundColor: theme[themeStyle].primary_orange,
                                                        color: theme[themeStyle].black,
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].primary_orange,
                                                        },
                                                    },
                                                },
                                            },
                                        },
                                    }}>
                                    <MenuItem value="head shot">Head Shot</MenuItem>
                                    <MenuItem value="audio reel">Audio Reel</MenuItem>
                                    <MenuItem value="demo reel">Demo Reel</MenuItem>
                                    <MenuItem value="fullbody shot">Full Body Shot</MenuItem>
                                    <MenuItem value="portfolio">Portfolio</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel sx={{ color: theme[themeStyle].tag_white }}>Required Skills</InputLabel>
                                <Select
                                    multiple
                                    value={editedValues.required_skill || []}
                                    onChange={e => handleInputChange('required_skill', e.target.value)}
                                    label="Required Skills"
                                    sx={{
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                        '& .MuiSelect-select': {
                                            backgroundColor: theme[themeStyle].gray_text,
                                        },
                                        '& .MuiSvgIcon-root': {
                                            color: theme[themeStyle].primary_white,
                                        },
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: theme[themeStyle].gray_text,
                                                color: theme[themeStyle].primary_white,
                                                '& .MuiMenuItem-root': {
                                                    color: theme[themeStyle].primary_white,
                                                    '&:hover': {
                                                        backgroundColor: theme[themeStyle].background_gradient.end,
                                                    },
                                                    '&.Mui-selected': {
                                                        backgroundColor: theme[themeStyle].primary_orange,
                                                        color: theme[themeStyle].black,
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].primary_orange,
                                                        },
                                                    },
                                                },
                                            },
                                        },
                                    }}>
                                    <MenuItem value="actor">Actor</MenuItem>
                                    <MenuItem value="actress">Actress</MenuItem>
                                    <MenuItem value="extra">Extra</MenuItem>
                                    <MenuItem value="model">Model</MenuItem>
                                    <MenuItem value="voice artist">Voice Artist</MenuItem>
                                    <MenuItem value="technical">Technical</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Minimum Age"
                                value={editedValues.min_age || ''}
                                onChange={e => handleInputChange('min_age', e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                }}
                                InputLabelProps={{
                                    style: {
                                        color: theme[themeStyle].tag_white,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Maximum Age"
                                value={editedValues.max_age || ''}
                                onChange={e => handleInputChange('max_age', e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                }}
                                InputLabelProps={{
                                    style: {
                                        color: theme[themeStyle].tag_white,
                                    },
                                }}
                            />
                        </Grid>
                    </Grid>
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].form_black }} />

            {/* Submission Report Settings */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader title="Submission Report Settings" section="submission_report" />

                {!editingSections.submission_report ? (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Report Frequency
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                {audition.submission_report || 'Daily digest'}
                            </Typography>
                        </Grid>
                    </Grid>
                ) : (
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                select
                                fullWidth
                                value={editedValues.submission_report || 'daily digest'}
                                onChange={e => handleInputChange('submission_report', e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                    '& .MuiSelect-select': {
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                }}
                                SelectProps={{
                                    MenuProps: {
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: theme[themeStyle].gray_text,
                                                color: theme[themeStyle].primary_white,
                                                '& .MuiMenuItem-root': {
                                                    color: theme[themeStyle].primary_white,
                                                    '&:hover': {
                                                        backgroundColor: theme[themeStyle].background_gradient.end,
                                                    },
                                                    '&.Mui-selected': {
                                                        backgroundColor: theme[themeStyle].primary_orange,
                                                        color: theme[themeStyle].black,
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].primary_orange,
                                                        },
                                                    },
                                                },
                                            },
                                        },
                                    },
                                    sx: {
                                        '& .MuiSvgIcon-root': {
                                            color: theme[themeStyle].primary_white,
                                        },
                                    },
                                }}>
                                <MenuItem value="daily digest">Daily Digest</MenuItem>
                                <MenuItem value="weekly digest">Weekly Digest</MenuItem>
                                <MenuItem value="monthly digest">Monthly Digest</MenuItem>
                                <MenuItem value="applications as they come in">Applications as they come in</MenuItem>
                            </TextField>
                        </Grid>
                    </Grid>
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].form_black }} />

            {/* Attachments */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader title="Attachments" section="attachments" />

                {!editingSections.attachments ? (
                    <Grid container spacing={2}>
                        {audition.attachment ? (
                            <Grid item xs={12}>
                                <Box
                                    sx={{
                                        p: 2,
                                        border: `1px solid ${theme[themeStyle].form_black}`,
                                        borderRadius: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                    }}>
                                    <PictureAsPdf
                                        sx={{
                                            fontSize: 40,
                                            color: theme[themeStyle].primary_orange,
                                        }}
                                    />
                                    <Box sx={{ flex: 1 }}>
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                color: theme[themeStyle].light_gray_text,
                                                mb: 1,
                                            }}>
                                            {audition.attachment_type || 'No type specified'}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                color: theme[themeStyle].primary_white,
                                                mb: 1,
                                            }}>
                                            {audition.attachment_description || 'No description provided'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <IconButton
                                            onClick={() => window.open(audition.attachment, '_blank')}
                                            sx={{
                                                color: theme[themeStyle].primary_orange,
                                                '&:hover': {
                                                    backgroundColor: 'rgba(255, 165, 0, 0.1)',
                                                },
                                            }}>
                                            <Visibility />
                                        </IconButton>
                                    </Box>
                                </Box>
                            </Grid>
                        ) : (
                            <Grid item xs={12}>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        color: theme[themeStyle].light_gray_text,
                                    }}>
                                    No attachment added
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                ) : (
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <Box
                                sx={{
                                    p: 2,
                                    border: `1px solid ${theme[themeStyle].form_black}`,
                                    borderRadius: 1,
                                }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <TextField
                                            select
                                            fullWidth
                                            label="Attachment Type"
                                            value={editedValues.attachment_type || ''}
                                            onChange={e => handleInputChange('attachment_type', e.target.value)}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    color: theme[themeStyle].tag_white,
                                                    backgroundColor: theme[themeStyle].gray_text,
                                                },
                                            }}
                                            InputLabelProps={{
                                                style: {
                                                    color: theme[themeStyle].tag_white,
                                                },
                                            }}
                                            SelectProps={{
                                                MenuProps: {
                                                    PaperProps: {
                                                        sx: {
                                                            backgroundColor: theme[themeStyle].gray_text,
                                                            color: theme[themeStyle].primary_white,
                                                            '& .MuiMenuItem-root': {
                                                                color: theme[themeStyle].primary_white,
                                                                '&:hover': {
                                                                    backgroundColor:
                                                                        theme[themeStyle].background_gradient.end,
                                                                },
                                                                '&.Mui-selected': {
                                                                    backgroundColor: theme[themeStyle].primary_orange,
                                                                    color: theme[themeStyle].black,
                                                                    '&:hover': {
                                                                        backgroundColor:
                                                                            theme[themeStyle].primary_orange,
                                                                    },
                                                                },
                                                            },
                                                        },
                                                    },
                                                },
                                                sx: {
                                                    '& .MuiSvgIcon-root': {
                                                        color: theme[themeStyle].primary_white,
                                                    },
                                                },
                                            }}>
                                            <MenuItem value="side">Side</MenuItem>
                                            <MenuItem value="details">Details</MenuItem>
                                            <MenuItem value="others">Others</MenuItem>
                                            <MenuItem value="pre-requisite">Pre-requisite</MenuItem>
                                            <MenuItem value="requirement">Requirement</MenuItem>
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={2}
                                            label="Description"
                                            InputLabelProps={{
                                                style: {
                                                    color: theme[themeStyle].tag_white,
                                                },
                                            }}
                                            value={editedValues.attachment_description || ''}
                                            onChange={e => handleInputChange('attachment_description', e.target.value)}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    color: theme[themeStyle].tag_white,
                                                    backgroundColor: theme[themeStyle].gray_text,
                                                },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        {editedValues.attachment ? (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 2,
                                                    p: 2,
                                                    border: `1px solid ${theme[themeStyle].form_black}`,
                                                    borderRadius: 1,
                                                }}>
                                                <PictureAsPdf
                                                    sx={{
                                                        fontSize: 40,
                                                        color: theme[themeStyle].primary_orange,
                                                    }}
                                                />
                                                <Typography
                                                    sx={{
                                                        flex: 1,
                                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                        color: theme[themeStyle].primary_white,
                                                    }}>
                                                    Current Attachment
                                                </Typography>
                                                <Button
                                                    variant="outlined"
                                                    startIcon={<Delete />}
                                                    onClick={() => handleInputChange('attachment', '')}
                                                    sx={{
                                                        borderColor: theme[themeStyle].primary_orange,
                                                        color: theme[themeStyle].primary_orange,
                                                        '&:hover': {
                                                            borderColor: theme[themeStyle].primary_orange,
                                                            backgroundColor: 'rgba(255, 165, 0, 0.1)',
                                                        },
                                                    }}>
                                                    Remove
                                                </Button>
                                            </Box>
                                        ) : (
                                            <>
                                                <input
                                                    type="file"
                                                    id="file-upload"
                                                    style={{ display: 'none' }}
                                                    onChange={e => {
                                                        const file = e.target.files?.[0];
                                                        if (file) {
                                                            handleInputChange('attachment', file.name);
                                                        }
                                                    }}
                                                />
                                                <Button
                                                    variant="outlined"
                                                    component="label"
                                                    htmlFor="file-upload"
                                                    startIcon={<UploadFile />}
                                                    sx={{
                                                        borderColor: theme[themeStyle].primary_orange,
                                                        color: theme[themeStyle].primary_orange,
                                                        '&:hover': {
                                                            borderColor: theme[themeStyle].primary_orange,
                                                            backgroundColor: 'rgba(255, 165, 0, 0.1)',
                                                        },
                                                    }}>
                                                    Upload File
                                                </Button>
                                            </>
                                        )}
                                    </Grid>
                                </Grid>
                            </Box>
                        </Grid>
                    </Grid>
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].form_black }} />
        </Box>
    );
};
